import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatOperatingHours } from '../../utils/operatingHours';
import LabelChips from '../common/LabelChips';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AccessTimeOutlined as HoursIcon,
  Close as CloseIcon,
  ContactPhoneOutlined as ContactIcon,
  DeleteOutline as ArchiveIcon,
  EditOutlined as EditIcon,
  InboxOutlined as EmptyIcon,
  Inventory2Outlined as MaterialIcon,
  LocationOnOutlined as LocationIcon,
  MapOutlined as MapIcon,
  NotesOutlined as NotesIcon,
  PhoneOutlined as PhoneIcon,
} from '@mui/icons-material';
import L from 'leaflet';
import { CircleMarker, MapContainer, TileLayer, Tooltip as MapTooltip, useMap } from 'react-leaflet';
import { APP_SHAPE } from '../../constants/shape';
import type { SupplierCollectionPoint } from '../../types/supplier.type';
import StatusChip from '../StatusChip';

interface SupplierCollectionPointsProps {
  points: SupplierCollectionPoint[];
  materialLabels: Map<string, string>;
  view: 'map' | 'table';
  canMutate?: boolean;
  onEdit?: (point: SupplierCollectionPoint) => void;
  onArchive?: (point: SupplierCollectionPoint) => void;
}

const DEFAULT_CENTER: [number, number] = [24.7136, 46.6753];

function FitCollectionPointBounds({ points }: { points: SupplierCollectionPoint[] }) {
  const map = useMap();

  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map((point) => [point.latitude!, point.longitude!]));
    map.fitBounds(bounds, { padding: [48, 48], maxZoom: 15 });
  }, [map, points]);

  return null;
}

function EmptyCollectionPoints({ mapView }: { mapView: boolean }) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        minHeight: mapView ? 520 : 360,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: 'action.hover',
      }}
    >
      <Stack alignItems="center" spacing={1.5} sx={{ maxWidth: 420, textAlign: 'center' }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.paper',
            color: 'text.secondary',
          }}
        >
          {mapView ? <MapIcon fontSize="large" /> : <EmptyIcon fontSize="large" />}
        </Box>
        <Typography variant="h6">{t('suppliers.collectionPoints.emptyTitle')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('suppliers.collectionPoints.emptyHint')}
        </Typography>
      </Stack>
    </Box>
  );
}

function PointDetailPanel({
  point,
  materialLabels,
  onClose,
}: {
  point: SupplierCollectionPoint;
  materialLabels: Map<string, string>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Paper
      elevation={3}
      component="section"
      aria-label={t('suppliers.collectionPoints.detail.ariaLabel', { name: point.name })}
      sx={{
        position: 'absolute',
        top: 2,
        insetInlineEnd: 2,
        bottom: 2,
        zIndex: 1000,
        width: { xs: 'calc(100% - 32px)', sm: 340 },
        maxWidth: 'calc(100% - 32px)',
        overflow: 'auto',
        borderRadius: `${APP_SHAPE.radiusLg}px`,
      }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6">{point.name}</Typography>
          {point.status && <Box sx={{ mt: 1 }}><StatusChip value={point.status} /></Box>}
        </Box>
        <Tooltip title={t('suppliers.collectionPoints.detail.close')}>
          <IconButton aria-label={t('suppliers.collectionPoints.detail.close')} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider />
      <Stack spacing={2.5} sx={{ p: 2.5 }}>
        <Detail icon={<LocationIcon />} label={t('suppliers.collectionPoints.detail.address')}>
          {point.address || `${point.latitude}, ${point.longitude}`}
        </Detail>
        {point.contact_name && <Detail icon={<ContactIcon />} label={t('suppliers.collectionPoints.detail.contact')}>{point.contact_name}</Detail>}
        {point.contact_phone && <Detail icon={<PhoneIcon />} label={t('suppliers.collectionPoints.detail.phone')}>{point.contact_phone}</Detail>}
        {point.operating_hours && (
          <Detail icon={<HoursIcon />} label={t('suppliers.collectionPoints.detail.operatingHours')}>
            {formatOperatingHours(point.operating_hours, (day) => t(`collectionSites.days.${day}`))}
          </Detail>
        )}
        {(point.labels?.length ?? 0) > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              {t('suppliers.collectionPoints.table.labels')}
            </Typography>
            <LabelChips labels={point.labels} />
          </Box>
        )}
        <Box>
          <Detail icon={<MaterialIcon />} label={t('suppliers.collectionPoints.detail.wasteTypes')}>
            {(point.material_ids?.length ?? 0) === 0 ? t('suppliers.collectionPoints.detail.notSpecified') : ''}
          </Detail>
          {(point.material_ids?.length ?? 0) > 0 && (
            <Stack direction="row" gap={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
              {point.material_ids?.map((id) => (
                <Chip key={id} size="small" label={materialLabels.get(id) ?? id} sx={{ borderRadius: `${APP_SHAPE.radiusSm}px` }} />
              ))}
            </Stack>
          )}
        </Box>
        {point.notes && <Detail icon={<NotesIcon />} label={t('suppliers.collectionPoints.detail.notes')}>{point.notes}</Detail>}
      </Stack>
    </Paper>
  );
}

function Detail({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '20px minmax(0, 1fr)', gap: 1.5 }}>
      <Box sx={{ color: 'text.secondary', display: 'flex', '& svg': { fontSize: 20 } }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
        <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{children}</Typography>
      </Box>
    </Box>
  );
}

export default function SupplierCollectionPoints({ points, materialLabels, view, canMutate, onEdit, onArchive }: SupplierCollectionPointsProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [selectedPoint, setSelectedPoint] = useState<SupplierCollectionPoint | null>(null);
  const locatedPoints = useMemo(
    () => points.filter((point) => Number.isFinite(point.latitude) && Number.isFinite(point.longitude)),
    [points],
  );

  useEffect(() => setSelectedPoint(null), [view]);

  if (view === 'table') {
    if (!points.length) return <EmptyCollectionPoints mapView={false} />;
    return (
      <TableContainer>
        <Table stickyHeader aria-label={t('suppliers.collectionPoints.table.name')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('suppliers.collectionPoints.table.name')}</TableCell>
              <TableCell>{t('suppliers.collectionPoints.table.address')}</TableCell>
              <TableCell>{t('suppliers.collectionPoints.table.contact')}</TableCell>
              <TableCell>{t('suppliers.collectionPoints.table.wasteTypes')}</TableCell>
              <TableCell>{t('suppliers.collectionPoints.table.labels')}</TableCell>
              <TableCell>{t('suppliers.collectionPoints.table.status')}</TableCell>
              {canMutate && <TableCell align="right">{t('suppliers.collectionPoints.table.actions')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {points.map((point) => (
              <TableRow key={point.id} hover>
                <TableCell><Typography variant="body2" fontWeight={600}>{point.name}</Typography></TableCell>
                <TableCell>{point.address || '—'}</TableCell>
                <TableCell>{point.contact_name || point.contact_phone || '—'}</TableCell>
                <TableCell>{point.material_ids?.length ?? 0}</TableCell>
                <TableCell><LabelChips labels={point.labels} /></TableCell>
                <TableCell>{point.status ? <StatusChip value={point.status} /> : '—'}</TableCell>
                {canMutate && (
                  <TableCell align="right">
                    {onEdit && (
                      <Tooltip title={t('suppliers.collectionPoints.table.edit')}>
                        <IconButton aria-label={`${t('suppliers.collectionPoints.table.edit')} ${point.name}`} size="small" onClick={() => onEdit(point)} sx={{ color: 'primary.main' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {onArchive && (
                      <Tooltip title={t('suppliers.collectionPoints.table.archive')}>
                        <IconButton aria-label={`${t('suppliers.collectionPoints.table.archive')} ${point.name}`} size="small" color="error" onClick={() => onArchive(point)}>
                          <ArchiveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (!locatedPoints.length) return <EmptyCollectionPoints mapView />;

  return (
    <Box sx={{ position: 'relative', minHeight: 520, bgcolor: 'action.hover' }}>
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={11}
        scrollWheelZoom
        style={{ width: '100%', height: 520 }}
        attributionControl
      >
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url={theme.palette.mode === 'dark'
            ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
            : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'}
        />
        <FitCollectionPointBounds points={locatedPoints} />
        {locatedPoints.map((point) => (
          <CircleMarker
            key={point.id}
            center={[point.latitude!, point.longitude!]}
            radius={10}
            pathOptions={{
              color: theme.palette.background.paper,
              weight: 3,
              fillColor: theme.palette.primary.main,
              fillOpacity: 1,
            }}
            eventHandlers={{ click: () => setSelectedPoint(point) }}
          >
            <MapTooltip direction="top" offset={[0, -8]}>{point.name}</MapTooltip>
          </CircleMarker>
        ))}
      </MapContainer>
      <Chip
        icon={<LocationIcon />}
        label={t('suppliers.collectionPoints.map.pointCount', { count: locatedPoints.length })}
        sx={{
          position: 'absolute',
          top: 2,
          insetInlineStart: 2,
          zIndex: 900,
          bgcolor: alpha(theme.palette.background.paper, 0.94),
          borderRadius: `${APP_SHAPE.radiusSm}px`,
        }}
      />
      {selectedPoint && (
        <PointDetailPanel point={selectedPoint} materialLabels={materialLabels} onClose={() => setSelectedPoint(null)} />
      )}
    </Box>
  );
}
